import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { pluck } from 'rxjs/operators';
import { LoadingSpaceType } from 'src/app/core/services/import-export-service-types';
import { ProjectService } from 'src/app/core/services/project.service';
import { shouldShowLdmInCargoSpaceSummary } from 'src/app/shared/helpers/pallet-loading-space.helper';
import { CargoSpace, CargoSpaceSummary, ProjectOptions } from '../../../../core/types';

@Component({
    selector: 'gl-loading-space-summary',
    templateUrl: './loading-space-summary.component.html',
    styleUrls: ['./loading-space-summary.component.scss'],
    standalone: false
})
export class LoadingSpaceSummaryComponent {
  @Input() public summary: CargoSpaceSummary;
  @Input() public cargoSpace: CargoSpace;
  @Input() public index: number;
  @Input() public isSet: boolean;
  @Input() public isColumnView = false;
  public unit: string;
  public isMobile: boolean;

  constructor(
    private projectService: ProjectService,
    public translate: TranslateService,
    private router: Router,
  ) {
    this.isMobile = this.router.url.includes('mobile');
    this.projectService.project
      .pipe(pluck('options'))
      .subscribe((options: ProjectOptions) => (this.unit = options?.unit));
  }

  public totalLDMTooltip(): string {
    if (this.summary.takenLdmVarioUp >= 0) {
      const totalUpperFloor = _.round(
        this.summary.freeLdmVarioUp + this.summary.takenLdmVarioUp,
        2,
      );
      const takenLowerFloor = _.round(this.summary.takenLdm - this.summary.takenLdmVarioUp, 2);
      const totalLowerFloor = _.round(
        takenLowerFloor + this.summary.freeLdm - this.summary.freeLdmVarioUp,
        2,
      );

      return `${this.translate.instant('upper_floor') as string}: ${
        this.summary.takenLdmVarioUp
      } / ${totalUpperFloor} LDM / ${
        this.translate.instant('lower_floor') as string
      }: ${takenLowerFloor} / ${totalLowerFloor} LDM`;
    }

    const total = _.round(this.summary.takenLdm + this.summary.freeLdm, 2);
    const percentOccupied = _.round((this.summary.takenLdm / total) * 100);
    return `${this.translate.instant('occupied') as string}: ${
      this.summary.takenLdm
    } (${percentOccupied}%) / ${this.translate.instant('total') as string}: ${total} LDM`;
  }

  public replicatedLoadTotal(): number | null {
    if (this.cargoSpace?.type !== LoadingSpaceType.Pallet) {
      return null;
    }
    const quantity = this.cargoSpace.extraFeatures?.palletQuantity ?? 0;
    if (quantity <= 1) {
      return null;
    }
    return (this.summary?.loadedLoads ?? 0) * quantity;
  }

  public isTotalWeightOverLimit() {
    return this.cargoSpace && this.cargoSpace.limit < this.summary.totalWeight;
  }

  public calculateLineWidthPercentage(plus?: number) {
    const percentage =
      (this.summary.takenLdm / (this.summary.freeLdm + this.summary.takenLdm)) * 100;
    if (percentage === 0) {
      return 0;
    } else if (plus) {
      return percentage + plus;
    } else {
      return percentage;
    }
  }

  public axiTooltip(index: number): string {
    if (!this.cargoSpace || !this.cargoSpace.axis || !this.cargoSpace.axis[index]) return '';
    const addedLoad = this.cargoSpace.axis[index].addedLoad;
    const maxLoad = this.cargoSpace.axis[index].maxLoad;
    const formattedLoad = _.isNumber(addedLoad) && !_.isNaN(addedLoad) ? _.round(addedLoad) : 0;
    return `${this.translate.instant('vehicle_axle_load') as string}: ${formattedLoad}/${maxLoad} ${
      this.unit === 'cm' || this.unit === 'mm'
        ? (this.translate.instant('units_kg') as string)
        : (this.translate.instant('units_lb') as string)
    }`;
  }

  public calculateAxiLineWidthPercentage(index: number, plus?: number) {
    if (!this.summary?.axis || !this.summary.axis[index]) {
      return 0;
    }

    const percentage: number =
      (this.summary.axis[index].addedLoad / this.summary.axis[index].maxLoad) * 100;
    if (percentage === 0) {
      return 0;
    } else if (plus) {
      return percentage + plus;
    } else {
      return percentage;
    }
  }

  public isSecondCargoSpace() {
    return this.index === 1;
  }

  public isAnyNotLoadedLoads() {
    return this.summary.notLoadedLoads > 0;
  }

  public calculateCBM() {
    const cargoCBM = _.round(
      (this.cargoSpace.dimensions.height *
        this.cargoSpace.dimensions.width *
        this.cargoSpace.dimensions.length) /
        1000000,
      2,
    );
    const percentOccupied = _.round((this.summary.occupiedVolume / cargoCBM) * 100);
    return `${this.translate.instant('loads_volume') as string}: ${
      this.summary.occupiedVolume
    } (${percentOccupied}%) / ${cargoCBM} CBM`;
  }

  public getAreaAndVolumeUnit() {
    return this.unit === 'cm' || this.unit === 'mm' ? 'm' : 'in';
  }

  public showLdm(): boolean {
    return shouldShowLdmInCargoSpaceSummary(this.cargoSpace);
  }
}
